import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { useAuth } from './auth/AuthProvider'
import { listUwbBagOrders, cancelUwbBagOrder, cdpCancelBagOrder, UwbBagOrder } from '../services/api'
import {
  canonicalUwbRegistrationId,
  requireShopifyResourceId,
  withUwbRegistration,
} from './uwbPrizeSafety'

// Per-individual United We Barry's status, shown inside the Challenge Data tab.
// Uses the loaded customer's email + the live service-token lookup proxy
// (/api/uwb/lookup) to show registration status + individual and team class
// counts. Admins can fulfill prizes for completed registrations. Named
// override operators additionally retain manual completion and fulfilled-prize
// recovery controls; ordinary prize access does not grant those powers.

interface Props {
  email?: string | null
  marianaTekData?: any
}

const Card = styled.div`
  background: white;
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`

const Title = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0 0 1rem 0;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const StatLabel = styled.span`
  font-size: 1.1rem;
  color: ${theme.color.text.grey};
`

const StatValue = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${theme.color.text.base};
`

const Pill = styled.span<{ kind: 'yes' | 'no' }>`
  display: inline-block;
  padding: 0.2rem 0.7rem;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 700;
  background: ${(p) => (p.kind === 'yes' ? '#d4edda' : '#fff3cd')};
  color: ${(p) => (p.kind === 'yes' ? '#155724' : '#856404')};
`

const Muted = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.grey};
`

const OverrideSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid ${theme.color.accent.primary};
`

const OverrideTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const OverrideButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 0.7rem 1.4rem;
  border-radius: 4px;
  font-size: 1.3rem;
  font-weight: 700;
  cursor: pointer;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const SecondaryButton = styled.button`
  background: white;
  color: ${theme.color.text.base};
  border: 2px solid ${theme.color.border.light};
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  font-size: 1.25rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: 0.6rem;

  &:hover { background: ${theme.color.background.light}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const WizardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 1rem;
`

const FieldLabel = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
`

const FieldInput = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.3rem;
`

const FieldSelect = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.3rem;
  background: white;
`

const RadioRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.8rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.3rem;

  &:hover { background: ${theme.color.background.light}; }
`

const InlineNotice = styled.div<{ kind: 'ok' | 'err' | 'info' | 'warn' }>`
  padding: 0.7rem 0.9rem;
  border-radius: 4px;
  font-size: 1.2rem;
  background: ${(p) =>
    p.kind === 'ok' ? '#d4edda' : p.kind === 'err' ? '#f8d7da' : p.kind === 'warn' ? '#fff3cd' : '#e2e3ff'};
  color: ${(p) =>
    p.kind === 'ok' ? '#155724' : p.kind === 'err' ? '#721c24' : p.kind === 'warn' ? '#856404' : '#2b2b6b'};
`

const StepLog = styled.div`
  background: ${theme.color.background.light};
  border-radius: 4px;
  padding: 0.7rem 0.9rem;
  font-size: 1.15rem;
  font-family: monospace;
  white-space: pre-wrap;
  max-height: 220px;
  overflow-y: auto;
`

const fmtDate = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString()
}

// ── Region / currency inference ─────────────────────────────────────
type Region = { country: 'US' | 'CA' | 'GB' | 'IE' | 'OTHER'; currency: 'USD' | 'GBP' | 'EUR'; shop: 'us' | 'uk' | 'ie' }

const UK_KEYWORDS = ['london', 'finsbury', 'central st giles', 'canada water', 'aldgate', 'canary wharf', 'soho', 'st paul', 'sw1', 'manchester', 'liverpool', 'queensway', 'old street', 'victoria', 'euston']
const IE_KEYWORDS = ['dublin']
const CA_KEYWORDS = ['toronto', 'vancouver', 'calgary', 'richmond']
const EU_KEYWORDS = ['berlin', 'frankfurt', 'milano', 'paris', 'zurich', 'le marais']

function inferRegion(marianaTekData: any, reg: any): Region {
  // Best signal: MT profile country on the first user object.
  const mtCountry = (
    marianaTekData?.data?.[0]?.attributes?.country
    || marianaTekData?.user?.country
    || marianaTekData?.country
    || ''
  ).toString().toUpperCase().trim()
  if (mtCountry === 'US' || mtCountry === 'USA' || mtCountry === 'UNITED STATES') {
    return { country: 'US', currency: 'USD', shop: 'us' }
  }
  if (mtCountry === 'CA' || mtCountry === 'CAN' || mtCountry === 'CANADA') {
    return { country: 'CA', currency: 'USD', shop: 'us' }
  }
  if (mtCountry === 'GB' || mtCountry === 'UK' || mtCountry === 'UNITED KINGDOM' || mtCountry === 'GREAT BRITAIN' || mtCountry === 'ENGLAND') {
    return { country: 'GB', currency: 'GBP', shop: 'uk' }
  }
  if (mtCountry === 'IE' || mtCountry === 'IRELAND') {
    return { country: 'IE', currency: 'EUR', shop: 'ie' }
  }

  // Fall back to registered_studio substring matching.
  const studio = (reg?.registered_studio || '').toLowerCase()
  if (studio) {
    if (UK_KEYWORDS.some((k) => studio.includes(k))) return { country: 'GB', currency: 'GBP', shop: 'uk' }
    if (IE_KEYWORDS.some((k) => studio.includes(k))) return { country: 'IE', currency: 'EUR', shop: 'ie' }
    if (CA_KEYWORDS.some((k) => studio.includes(k))) return { country: 'CA', currency: 'USD', shop: 'us' }
    if (EU_KEYWORDS.some((k) => studio.includes(k))) return { country: 'OTHER', currency: 'EUR', shop: 'ie' }
  }

  return { country: 'US', currency: 'USD', shop: 'us' }
}

// Mirror UnitedWeBarrysButton.tsx UWB_STUDIOS for the UK/IE pickup dropdown.
const UWB_PICKUP_STUDIOS: { region: string; country: 'GB' | 'IE'; studios: { name: string; id: number }[] }[] = [
  {
    region: 'London',
    country: 'GB',
    studios: [
      { name: 'London — Central (Euston)', id: 9832 },
      { name: 'London — East (Old Street)', id: 9834 },
      { name: 'London — West (Queensway)', id: 9835 },
      { name: 'London — SW1 (Victoria)', id: 9836 },
      { name: 'London — Soho', id: 9840 },
      { name: "London — St. Paul's", id: 9833 },
      { name: 'London — Canary Wharf', id: 9837 },
    ],
  },
  {
    region: 'UK',
    country: 'GB',
    studios: [
      { name: 'Manchester', id: 9838 },
      { name: 'Liverpool', id: 9931 },
    ],
  },
  {
    region: 'Ireland',
    country: 'IE',
    studios: [{ name: 'Dublin — Dawson Street', id: 10134 }],
  },
]

// Map the registration's stored team_completed_track string to the actual
// min_count (number of classes) for that track. Parsing the first digit of
// the track key (the legacy behavior) was wrong: e.g. "team_4_track_2" used
// to return 4 (team-size), and "indv_track_1" used to return 1 (track index).
const TRACK_KEY_TO_MIN_COUNT: Record<string, number> = {
  indv_track_1: 8,
  indv_track_2: 12,
  indv_track_3: 20,
  team_2_track_1: 20,
  team_2_track_2: 40,
  team_4_track_1: 40,
  team_4_track_2: 80,
}

function trackMinCountFromKey(key?: string | null): number | null {
  if (!key) return null
  const direct = TRACK_KEY_TO_MIN_COUNT[String(key).trim()]
  return typeof direct === 'number' ? direct : null
}

// Resolve the min_count for a registration. Prize tier follows the tier the
// finisher actually achieved, NOT the tier they registered for — a customer
// who signed up for the 12-class solo track and went on to finish 20+ classes
// gets the 20-class prize ($90 / full tote), not the 12-class one ($50 /
// washbag). The CDP completion engine writes the achieved tier into
// team_completed_track (post-completion) and team_current_track (mid-flight)
// as the human-readable display name (e.g. "20+Classes", "12-19 Classes"),
// so we parse the leading digits out of either. selected_min_count is only
// used pre-completion when no achievement signal exists yet.
function trackMinCountFromRegistration(reg: any): number | null {
  if (!reg) return null
  const parseDisplay = (raw: string | undefined | null): number | null => {
    const display = String(raw || '').trim()
    if (!display) return null
    const m = display.match(/(\d+)/)
    if (m) {
      const parsed = Number(m[1])
      if (Number.isFinite(parsed) && parsed > 0) return parsed
    }
    const keyed = trackMinCountFromKey(display)
    return keyed || null
  }
  // 1. Achieved tier (post-completion). Authoritative for prize selection.
  const completed = parseDisplay(reg.team_completed_track)
  if (completed != null) return completed
  // 2. Achieved tier so far (mid-flight). Catches override flows kicked off
  //    before the engine has stamped team_completed_at.
  const current = parseDisplay(reg.team_current_track)
  if (current != null) return current
  // 3. Registered numeric (fallback before any achievement signal exists).
  const selMin = Number(reg.selected_min_count)
  if (Number.isFinite(selMin) && selMin > 0) return selMin
  // 4. Legacy: registered display name.
  return parseDisplay(reg.selected_track)
}

// Resolve the prize tier for a track. The (team_size, min_count) pair maps
// to one of three tiers — same min_count means different amounts depending
// on team size (e.g. 20 classes solo = top tier $90; 20 classes team-of-2 =
// mid tier $50). Returns null for unknown combinations.
//   solo:        8 → base | 12 → mid | 20 → top
//   team of 2:  20 → mid  | 40 → top
//   team of 4:  40 → mid  | 80 → top
type Tier = 'base' | 'mid' | 'top' | null
function trackTierFor(teamSize: number, minCount: number): Tier {
  if (teamSize === 1) {
    if (minCount === 8) return 'base'
    if (minCount === 12) return 'mid'
    if (minCount === 20) return 'top'
  }
  if (teamSize === 2) {
    if (minCount === 20) return 'mid'
    if (minCount === 40) return 'top'
  }
  if (teamSize === 4) {
    if (minCount === 40) return 'mid'
    if (minCount === 80) return 'top'
  }
  return null
}

// Prize matrix: gift card amounts per (country, tier). Canonical values from
// the UWB 2026 prize matrix (CDP lambdas/shared/src/uwb_prize_matrix.ts).
// Bag is offered at mid + top tiers; solo 8-class (base) is gift-card only.
function giftCardAmountFor(region: Region, teamSize: number, minCount: number): number {
  const tier = trackTierFor(teamSize, minCount)
  if (region.country === 'GB') {
    if (tier === 'top') return 70
    if (tier === 'mid') return 40
    return 20
  }
  if (region.country === 'IE' || region.country === 'OTHER') {
    if (tier === 'top') return 70
    if (tier === 'mid') return 40
    return 20
  }
  // US / CA
  if (tier === 'top') return 90
  if (tier === 'mid') return 50
  return 30
}

function bagOfferedFor(teamSize: number, minCount: number): boolean {
  if (teamSize > 1) return true
  return minCount >= 12
}

function skuForBag(teamSize: number, minCount: number): string {
  // The canonical CDP matrix gives every mid-tier track the washbag.
  if (trackTierFor(teamSize, minCount) === 'mid') return 'BFABARRYSWASHBAG'
  return 'BFABARRYSTOTE'
}

type Mode =
  | 'idle'
  | 'track'
  | 'prize'
  | 'details'
  | 'confirm'
  | 'running'
  | 'done'
  | 'change_prize'

interface ShippingForm {
  address1: string
  address2: string
  city: string
  state: string
  postal_code: string
  country: 'US' | 'CA'
  phone: string
}

interface TrackOption {
  min_count: number
  label: string
}

const UwbStatusCard: React.FC<Props> = ({ email, marianaTekData }) => {
  const { user, isAdmin } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [data, setData] = useState<{ registered: boolean; registration: any } | null>(null)
  const [configured, setConfigured] = useState<boolean | null>(null)
  const [adminOverrideConfigured, setAdminOverrideConfigured] = useState(false)
  const [canAdminOverride, setCanAdminOverride] = useState(false)
  const [finisher, setFinisher] = useState<any | null>(null)
  const [finisherLoading, setFinisherLoading] = useState(false)

  // Override wizard state
  const [tracksByTeamSize, setTracksByTeamSize] = useState<Record<string, TrackOption[]>>({})
  const [mode, setMode] = useState<Mode>('idle')
  const [selectedTrackMinCount, setSelectedTrackMinCount] = useState<number | null>(null)
  const [selectedPrizeCode, setSelectedPrizeCode] = useState<'gift_card' | 'bag' | null>(null)
  const [shippingForm, setShippingForm] = useState<ShippingForm>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
  })
  const [pickupStudioId, setPickupStudioId] = useState<number | null>(null)
  const [stepLog, setStepLog] = useState<string[]>([])
  const [wizardError, setWizardError] = useState<string>('')

  // Change-prize specific state
  const [existingGiftCards, setExistingGiftCards] = useState<any[] | null>(null)
  const [giftCardCheckLoading, setGiftCardCheckLoading] = useState(false)
  // Manual-paste fallback: admin can paste a card id we couldn't auto-match
  // (e.g. card issued to a different email or via a non-admin-site path).
  const [manualCardId, setManualCardId] = useState<string>('')
  const [manualCardCurrency, setManualCardCurrency] = useState<'USD' | 'GBP' | 'EUR'>('USD')

  // UWB bag-order change-prize state — surfaces existing prize-bag Shopify
  // orders (fan-out across US/UK/IE) so the admin can cancel before swapping.
  const [uwbOrders, setUwbOrders] = useState<UwbBagOrder[]>([])
  const [uwbOrdersLoading, setUwbOrdersLoading] = useState(false)
  const [uwbOrdersError, setUwbOrdersError] = useState<string | null>(null)
  const [cancelingOrderId, setCancelingOrderId] = useState<string | null>(null)

  // Fetch UWB config (tracks) once.
  useEffect(() => {
    fetch('/api/uwb/config')
      .then((r) => r.json())
      .then((c) => {
        if (c?.tracks) setTracksByTeamSize(c.tracks)
        setAdminOverrideConfigured(c?.adminOverrideConfigured === true)
        setCanAdminOverride(c?.canAdminOverride === true)
      })
      .catch(() => { /* non-fatal */ })
  }, [])

  // Lookup the registration whenever email changes.
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!email) {
        setData(null)
        return
      }
      setLoading(true)
      setError('')
      setData(null)
      try {
        const res = await fetch(`/api/uwb/lookup?email=${encodeURIComponent(email)}`)
        const body = await res.json()
        if (cancelled) return
        if (res.status === 503) {
          setConfigured(false)
          return
        }
        setConfigured(true)
        if (res.status === 404 && !body?.profile) {
          setData({ registered: false, registration: null })
        } else if (!res.ok && !body?.profile) {
          setError(body?.message || body?.error || `Lookup failed (${res.status})`)
        } else {
          setData({ registered: !!body.registered, registration: body.registration })
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Lookup failed')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [email])

  // Fetch finisher email status only when they've actually finished.
  useEffect(() => {
    let cancelled = false
    const reg = data?.registration
    if (!email || !data?.registered || !reg?.team_completed_at) {
      setFinisher(null)
      return
    }
    setFinisherLoading(true)
    fetch(`/api/uwb/finisher-email-status?email=${encodeURIComponent(email)}`)
      .then((r) => r.json())
      .then((body) => {
        if (cancelled) return
        setFinisher(body)
      })
      .catch(() => {
        if (cancelled) return
        setFinisher({ found: false, reason: 'lookup failed' })
      })
      .finally(() => {
        if (!cancelled) setFinisherLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [email, data])

  // Prefill the shipping form / pickup studio when entering the details step.
  // Precedence (highest wins so we never clobber what the admin has typed):
  //   1. Admin-typed values already in form state
  //   2. Customer-submitted values previously persisted in CDP (reg.prize_shipping_* / prize_pickup_*)
  //   3. MT profile attributes (marianaTekData)
  useEffect(() => {
    // Fire when entering details OR confirm with prize=bag — Continue-fulfillment
    // routes directly to 'confirm' when the customer-submitted CDP details are
    // already persisted, so confirm must also trigger the prefill.
    if ((mode !== 'details' && mode !== 'confirm') || selectedPrizeCode !== 'bag') return
    const reg = data?.registration
    setShippingForm((prev) => {
      // Don't clobber values the admin may already have typed.
      if (prev.address1 || prev.city || prev.postal_code) return prev
      // Preseed from customer-submitted CDP values if present.
      if (reg?.prize_shipping_address1 || reg?.prize_shipping_city || reg?.prize_shipping_postal_code) {
        const cdpRawCountry = String(reg.prize_shipping_country || '').toUpperCase()
        const cdpCountry: 'US' | 'CA' =
          cdpRawCountry === 'CA' || cdpRawCountry === 'CAN' || cdpRawCountry === 'CANADA' ? 'CA' : 'US'
        return {
          address1: reg.prize_shipping_address1 || '',
          address2: reg.prize_shipping_address2 || '',
          city: reg.prize_shipping_city || '',
          state: reg.prize_shipping_state || '',
          postal_code: reg.prize_shipping_postal_code || '',
          country: cdpCountry,
          phone: reg.prize_shipping_phone || '',
        }
      }
      // Fall back to MT profile attrs only when nothing's been captured yet.
      const attrs = marianaTekData?.data?.[0]?.attributes
      if (!attrs) return prev
      const rawCountry = String(attrs.country || '').toUpperCase()
      const country: 'US' | 'CA' = rawCountry === 'CA' || rawCountry === 'CAN' || rawCountry === 'CANADA' ? 'CA' : 'US'
      return {
        address1: attrs.address_line1 || '',
        address2: attrs.address_line2 || '',
        city: attrs.city || '',
        state: attrs.state_province || '',
        postal_code: attrs.postal_code || '',
        country,
        phone: attrs.phone_number || '',
      }
    })
    // Pickup studio preseed (UK/IE).
    if (reg?.prize_pickup_studio_id) {
      setPickupStudioId((prev) => (prev == null ? Number(reg.prize_pickup_studio_id) : prev))
    }
  }, [mode, selectedPrizeCode, marianaTekData, data])

  const reg = data?.registration

  // ── Derived: registration-shape helpers (must run before any early return) ──
  const region = useMemo<Region | null>(() => (reg ? inferRegion(marianaTekData, reg) : null), [marianaTekData, reg])

  // Stay quiet if the integration isn't configured on this environment.
  if (configured === false) return null

  const teamSize = reg?.team_size || 1
  const trackOptions: TrackOption[] = tracksByTeamSize[String(teamSize)] || []
  const registrationId: string | null = reg?.id || reg?.registration_id || null
  const isFulfilled = !!reg?.prize_fulfilled_at
  const hasPrize = !!reg?.prize_selected
  const isCompleted = !!reg?.team_completed_at

  const appendLog = (line: string) => setStepLog((prev) => [...prev, `${new Date().toLocaleTimeString()} — ${line}`])

  const resetWizard = () => {
    setMode('idle')
    setSelectedTrackMinCount(null)
    setSelectedPrizeCode(null)
    setShippingForm({ address1: '', address2: '', city: '', state: '', postal_code: '', country: 'US', phone: '' })
    setPickupStudioId(null)
    setStepLog([])
    setWizardError('')
    setExistingGiftCards(null)
    setManualCardId('')
    setManualCardCurrency('USD')
    setUwbOrders([])
    setUwbOrdersLoading(false)
    setUwbOrdersError(null)
    setCancelingOrderId(null)
  }

  // ── Wizard entry points ────────────────────────────────────────
  const startOverride = () => {
    if (!canAdminOverride && (!isAdmin || !isCompleted || isFulfilled)) return
    // Default: a clean wizard. For the "continue fulfillment" path
    // (completed + prize chosen + not yet fulfilled) we instead preseed
    // wizard state from the existing registration so the confirm screen
    // doesn't render "award null" and Submit doesn't hit the
    // "Pick a prize before submitting" guard.
    if (isCompleted && hasPrize && !isFulfilled) {
      // Preseed from the loaded registration.
      const existingPrize: 'gift_card' | 'bag' | null =
        reg?.prize_selected === 'bag' ? 'bag'
        : reg?.prize_selected === 'gift_card' ? 'gift_card'
        : null
      const trackMin = trackMinCountFromRegistration(reg)
      setMode('idle') // reset first so the prefill effect re-runs cleanly
      setSelectedPrizeCode(existingPrize)
      setSelectedTrackMinCount(trackMin)
      // IMPORTANT: do NOT reset shippingForm / pickupStudioId here. The prefill
      // useEffect below populates them from reg.prize_shipping_* /
      // prize_pickup_studio_id (the CDP-persisted customer-submitted values).
      // Resetting them to empty strings/null caused the Continue-fulfillment
      // confirm screen to submit empty shipping to /api/shopify/uwb-bag-order.
      setStepLog([])
      setWizardError('')
      setExistingGiftCards(null)
      // For bag prizes that still need shipping/pickup details, route to details.
      const needsDetails =
        existingPrize === 'bag'
        && !reg?.prize_shipping_address1
        && !reg?.prize_pickup_studio_id
      setMode(needsDetails ? 'details' : 'confirm')
      return
    }
    resetWizard()
    // If they're already completed, skip straight to prize selection.
    if (isCompleted && !hasPrize) setMode('prize')
    else if (isCompleted && hasPrize && isFulfilled) setMode('change_prize')
    else setMode('track')
  }

  const startChangePrize = async () => {
    if (!canAdminOverride) return
    resetWizard()
    // Bag already shipped/fulfilled OR gift card already issued → enter the
    // change_prize picker. Both paths now fan out across (a) Shopify gift cards
    // for this email AND (b) any UWB prize-bag Shopify orders for this email
    // across US/UK/IE. Each lookup is cheap and either may surface a captain-
    // vs-team-member mismatch the other would miss.
    const fulfilledBranch =
      isFulfilled && (reg?.prize_selected === 'bag' || reg?.prize_selected === 'gift_card')

    if (fulfilledBranch) {
      setMode('change_prize')
      // Seed the manual-paste currency dropdown from the inferred region so
      // an admin pasting a card id doesn't have to set it explicitly in the
      // common case.
      if (region?.currency) setManualCardCurrency(region.currency)

      // Kick off gift-card lookup (existing behavior).
      setGiftCardCheckLoading(true)
      const giftCardLookup = (async () => {
        try {
          const res = await fetch(`/api/shopify/gift-cards?email=${encodeURIComponent(email || '')}`)
          const body = await res.json()
          const giftCards = body?.giftCards || []
          setExistingGiftCards(giftCards)
          appendLog(`fetched ${giftCards.length} Shopify gift card${giftCards.length === 1 ? '' : 's'} for ${email || '(no email)'}`)
        } catch (e: any) {
          setExistingGiftCards([])
          appendLog(`✗ gift-card lookup failed: ${e?.message || 'unknown error'}`)
        } finally {
          setGiftCardCheckLoading(false)
        }
      })()

      // Kick off UWB bag-order lookup (new behavior).
      setUwbOrdersLoading(true)
      setUwbOrdersError(null)
      const uwbOrderLookup = (async () => {
        try {
          const r = await listUwbBagOrders(email || '', adminEmail)
          setUwbOrders(r.orders || [])
          appendLog(`fetched ${r.orders.length} UWB bag order${r.orders.length === 1 ? '' : 's'} for ${email || '(no email)'}`)
        } catch (e: any) {
          setUwbOrders([])
          setUwbOrdersError(e?.message || 'UWB bag-order lookup failed')
          appendLog(`✗ UWB bag-order lookup failed: ${e?.message || 'unknown error'}`)
        } finally {
          setUwbOrdersLoading(false)
        }
      })()

      await Promise.all([giftCardLookup, uwbOrderLookup])
      return
    }
    // Otherwise (no fulfillment yet) we can swap freely — go pick a new prize.
    setMode('prize')
  }

  // ── Submission orchestration ───────────────────────────────────
  const adminEmail = user?.email || 'admin-site'

  const postJSON = async (path: string, body: any) => {
    const res = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
    let parsed: any = null
    try { parsed = await res.json() } catch { parsed = null }
    return { ok: res.ok, status: res.status, body: parsed }
  }

  const runSubmit = async () => {
    if (!canAdminOverride && (!isAdmin || !isCompleted || isFulfilled)) {
      setWizardError('This action requires an authorized challenge override operator.')
      return
    }
    if (!registrationId) {
      setWizardError('Registration id missing — cannot submit.')
      return
    }
    let prizeRegistrationId: string
    try {
      prizeRegistrationId = canonicalUwbRegistrationId(registrationId)
    } catch (error: any) {
      setWizardError(error?.message || 'A valid registration id is required.')
      return
    }
    if (!region) {
      setWizardError('Region not yet resolved.')
      return
    }
    if (!selectedPrizeCode) {
      setWizardError('Pick a prize before submitting.')
      return
    }
    // Snapshot the change-prize intent BEFORE we transition to 'running'.
    // The previous implementation derived `force` from `mode === 'running'`,
    // which is never true at submit time (mode is 'confirm' or 'details'),
    // so force was always false and the CDP 409'd on
    // change-prize-after-fulfillment. Instead derive from the loaded
    // registration: the admin is doing a change-after-fulfillment iff the
    // registration is fulfilled AND the chosen prize differs from the
    // already-selected prize.
    const isChangeAfterFulfillment =
      !!reg?.prize_fulfilled_at
      && !!reg?.prize_selected
      && reg.prize_selected !== selectedPrizeCode

    setMode('running')
    setStepLog([])
    setWizardError('')

    const trackForFinish = selectedTrackMinCount ?? trackMinCountFromRegistration(reg)

    try {
      // (a) Complete the registration if not already.
      if (!isCompleted) {
        if (!selectedTrackMinCount) {
          throw new Error('No track selected for the complete step.')
        }
        appendLog(`POST /api/uwb/admin/registration/${registrationId}/complete`)
        const r1 = await postJSON(`/api/uwb/admin/registration/${registrationId}/complete`, {
          track_min_count: selectedTrackMinCount,
          team_size: teamSize,
        })
        if (!r1.ok) {
          throw new Error(`complete failed (${r1.status}): ${r1.body?.error || r1.body?.message || 'unknown'}`)
        }
        appendLog(`  ✓ complete OK`)
      } else {
        appendLog(`(team already completed — skipping complete step)`)
      }

      // (b) Select the prize.
      if (reg?.prize_order_canceled_at) {
        appendLog(`(prior bag order ${reg.prize_shopify_order_id} on ${reg.prize_shopify_shop} canceled at ${fmtDate(reg.prize_order_canceled_at)})`)
      }
      appendLog(`POST /api/uwb/admin/registration/${registrationId}/select-prize`)
      const r2 = await postJSON(`/api/uwb/admin/registration/${registrationId}/select-prize`, {
        prize_code: selectedPrizeCode,
        ...(canAdminOverride ? { force: isChangeAfterFulfillment } : { email }),
      })
      if (!r2.ok) {
        throw new Error(`select-prize failed (${r2.status}): ${r2.body?.error || r2.body?.message || 'unknown'}`)
      }
      appendLog(`  ✓ prize selected: ${selectedPrizeCode}`)

      // (c) Capture details if bag.
      if (selectedPrizeCode === 'bag') {
        const detailsBody: any = region.country === 'GB' || region.country === 'IE'
          ? {
              pickup_studio_id: pickupStudioId,
              pickup_studio_name: UWB_PICKUP_STUDIOS.flatMap((g) => g.studios).find((s) => s.id === pickupStudioId)?.name || '',
            }
          : { shipping: { ...shippingForm } }
        if (!canAdminOverride) detailsBody.email = email
        appendLog(`POST /api/uwb/admin/registration/${registrationId}/details`)
        const r3 = await postJSON(`/api/uwb/admin/registration/${registrationId}/details`, detailsBody)
        if (!r3.ok) {
          throw new Error(`details failed (${r3.status}): ${r3.body?.error || r3.body?.message || 'unknown'}`)
        }
        appendLog(`  ✓ details captured`)
      }

      // (d) Create the Shopify object (gift card OR bag order).
      let shopifyOrderId: number | string | null = null
      let shopifyShop: string | null = null
      let giftCardSummary: string | null = null
      const minCountForPrize = trackForFinish || selectedTrackMinCount || 12
      if (selectedPrizeCode === 'gift_card') {
        const amount = giftCardAmountFor(region, teamSize, minCountForPrize)
        appendLog(`POST /api/shopify/uwb-gift-card (${region.currency} ${amount})`)
        const r4 = await postJSON(canAdminOverride ? '/api/shopify/uwb-gift-card' : '/api/uwb/prize/gift-card', canAdminOverride ? withUwbRegistration(prizeRegistrationId, {
          email,
          firstName: marianaTekData?.data?.[0]?.attributes?.first_name || '',
          lastName: marianaTekData?.data?.[0]?.attributes?.last_name || '',
          amount: amount.toFixed(2),
          currency: region.currency,
        }) : { registration_id: prizeRegistrationId, email })
        if (!r4.ok) {
          throw new Error(`uwb-gift-card failed (${r4.status}): ${r4.body?.error || 'unknown'}`)
        }
        const gc = r4.body?.giftCard
        const giftCardId = requireShopifyResourceId(gc?.id, 'UWB gift card')
        giftCardSummary = `card ${giftCardId}, code ending ${gc?.code_last4 || String(gc?.code || '').slice(-4) || 'unknown'}, ${gc?.currency || region.currency} ${amount}`
        appendLog(`  ✓ gift card issued (${giftCardSummary})`)
      } else {
        // bag
        const sku = skuForBag(teamSize, minCountForPrize)
        const shopifyPayload: any = withUwbRegistration(prizeRegistrationId, {
          shop: region.shop,
          sku,
          quantity: 1,
          currency: region.currency,
          customer: {
            email,
            first_name: marianaTekData?.data?.[0]?.attributes?.first_name || '',
            last_name: marianaTekData?.data?.[0]?.attributes?.last_name || '',
            phone: marianaTekData?.data?.[0]?.attributes?.phone_number || shippingForm.phone || '',
          },
          note: `UWB 2026 prize - admin override by ${adminEmail} (reg ${prizeRegistrationId})`,
          tags: ['UWB-2026', 'UWB-prize-bag', `track:${teamSize}x${minCountForPrize}`, 'uwb-admin-override'],
          free_shipping: true,
        })
        if (region.country === 'GB' || region.country === 'IE') {
          const studioName = UWB_PICKUP_STUDIOS.flatMap((g) => g.studios).find((s) => s.id === pickupStudioId)?.name || ''
          shopifyPayload.pickup = { studio_id: pickupStudioId, studio_name: studioName }
        } else {
          shopifyPayload.shipping = { ...shippingForm }
        }
        appendLog(`POST /api/shopify/uwb-bag-order (${region.shop}, ${sku})`)
        const normalDelivery = region.country === 'GB' || region.country === 'IE'
          ? { pickup_studio_id: pickupStudioId }
          : { shipping: { ...shippingForm } }
        const r4 = await postJSON(
          canAdminOverride ? '/api/shopify/uwb-bag-order' : '/api/uwb/prize/bag-order',
          canAdminOverride ? shopifyPayload : { registration_id: prizeRegistrationId, email, ...normalDelivery },
        )
        if (!r4.ok) {
          throw new Error(`uwb-bag-order failed (${r4.status}): ${r4.body?.error || 'unknown'}`)
        }
        // The server returns only a verified Shopify order identity and store.
        const orderBody = r4.body
        shopifyOrderId = requireShopifyResourceId(orderBody?.shopify_order_id, 'UWB order')
        shopifyShop = orderBody?.shop || region.shop
        appendLog(`  ✓ bag order created (id ${shopifyOrderId ?? 'unknown'})`)
      }

      // (e) Mark fulfilled in the CDP.
      const fulfillBody: any = canAdminOverride ? {
        fulfilled_by: adminEmail,
        notes: selectedPrizeCode === 'gift_card'
          ? `Override via admin tool — gift card ${giftCardSummary || ''}`
          : `Override via admin tool — order ${shopifyOrderId ?? '(no id)'}`,
      } : { email, notes: 'Earned prize fulfilled via Admin Tool' }
      if (canAdminOverride && selectedPrizeCode === 'bag' && shopifyOrderId !== null) {
        fulfillBody.shopify_order_id = shopifyOrderId
        fulfillBody.shopify_shop = shopifyShop
      }
      appendLog(`POST /api/uwb/admin/registration/${registrationId}/fulfill`)
      const r5 = await postJSON(`/api/uwb/admin/registration/${registrationId}/fulfill`, fulfillBody)
      if (!r5.ok) {
        throw new Error(`fulfill failed (${r5.status}): ${r5.body?.error || 'unknown'}`)
      }
      appendLog(`  ✓ fulfilled`)

      setMode('done')

      // Refresh the registration card.
      if (email) {
        try {
          const refreshed = await fetch(`/api/uwb/lookup?email=${encodeURIComponent(email)}`).then((r) => r.json())
          setData({ registered: !!refreshed.registered, registration: refreshed.registration })
        } catch { /* ignore */ }
      }
    } catch (e: any) {
      setWizardError(e?.message || 'Override failed')
      appendLog(`✗ ${e?.message || 'failed'}`)
    }
  }

  // ── Renderers ───────────────────────────────────────────────────
  const renderTrackStep = () => (
    <WizardBody>
      <FieldLabel>Pick the track to credit this {teamSize > 1 ? `team of ${teamSize}` : 'solo registrant'} with:</FieldLabel>
      {trackOptions.length === 0 ? (
        <InlineNotice kind="warn">No track options loaded — /api/uwb/config returned empty.</InlineNotice>
      ) : (
        trackOptions.map((t) => (
          <RadioRow key={t.min_count}>
            <input
              type="radio"
              name="uwb-track"
              checked={selectedTrackMinCount === t.min_count}
              onChange={() => setSelectedTrackMinCount(t.min_count)}
            />
            <span>{t.label} ({t.min_count} classes)</span>
          </RadioRow>
        ))
      )}
      <div>
        <OverrideButton disabled={!selectedTrackMinCount} onClick={() => setMode('prize')}>Next: choose prize</OverrideButton>
        <SecondaryButton onClick={resetWizard}>Cancel</SecondaryButton>
      </div>
    </WizardBody>
  )

  const renderPrizeStep = () => {
    if (!region) return null
    const minCountForPrize = selectedTrackMinCount
      || trackMinCountFromRegistration(reg)
      || 12
    const gcAmount = giftCardAmountFor(region, teamSize, minCountForPrize)
    const offerBag = bagOfferedFor(teamSize, minCountForPrize)
    return (
      <WizardBody>
        <FieldLabel>
          Prize options for {region.country} / {teamSize === 1 ? `solo ${minCountForPrize}-class` : `team-of-${teamSize} ${minCountForPrize}-class`} finisher:
        </FieldLabel>
        <RadioRow>
          <input
            type="radio"
            name="uwb-prize"
            checked={selectedPrizeCode === 'gift_card'}
            onChange={() => setSelectedPrizeCode('gift_card')}
          />
          <span>Gift card — {region.currency} {gcAmount}</span>
        </RadioRow>
        {offerBag && (
          <RadioRow>
            <input
              type="radio"
              name="uwb-prize"
              checked={selectedPrizeCode === 'bag'}
              onChange={() => setSelectedPrizeCode('bag')}
            />
            <span>Barry's bag ({skuForBag(teamSize, minCountForPrize)})</span>
          </RadioRow>
        )}
        {!offerBag && (
          <InlineNotice kind="info">Solo 8-class finishers are gift-card only.</InlineNotice>
        )}
        <div>
          <OverrideButton
            disabled={!selectedPrizeCode}
            onClick={() => {
              if (selectedPrizeCode === 'bag') setMode('details')
              else setMode('confirm')
            }}
          >
            Next
          </OverrideButton>
          <SecondaryButton onClick={resetWizard}>Cancel</SecondaryButton>
        </div>
      </WizardBody>
    )
  }

  const renderDetailsStep = () => {
    if (!region) return null
    const isUkOrIe = region.country === 'GB' || region.country === 'IE'
    return (
      <WizardBody>
        {isUkOrIe ? (
          <>
            <FieldLabel>Pickup studio</FieldLabel>
            <FieldSelect
              value={pickupStudioId ?? ''}
              onChange={(e) => setPickupStudioId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">— Select a studio —</option>
              {UWB_PICKUP_STUDIOS.filter((g) => g.country === region.country).map((g) => (
                <optgroup key={g.region} label={g.region}>
                  {g.studios.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </optgroup>
              ))}
            </FieldSelect>
          </>
        ) : (
          <>
            <FieldLabel>Shipping address (all fields except line 2 are required)</FieldLabel>
            <FieldInput placeholder="Address line 1 *" value={shippingForm.address1} onChange={(e) => setShippingForm((s) => ({ ...s, address1: e.target.value }))} />
            <FieldInput placeholder="Address line 2 (optional)" value={shippingForm.address2} onChange={(e) => setShippingForm((s) => ({ ...s, address2: e.target.value }))} />
            <FieldInput placeholder="City *" value={shippingForm.city} onChange={(e) => setShippingForm((s) => ({ ...s, city: e.target.value }))} />
            <FieldInput placeholder="State / Province *" value={shippingForm.state} onChange={(e) => setShippingForm((s) => ({ ...s, state: e.target.value }))} />
            <FieldInput placeholder="Postal code *" value={shippingForm.postal_code} onChange={(e) => setShippingForm((s) => ({ ...s, postal_code: e.target.value }))} />
            <FieldSelect value={shippingForm.country} onChange={(e) => setShippingForm((s) => ({ ...s, country: e.target.value === 'CA' ? 'CA' : 'US' }))}>
              <option value="US">US</option>
              <option value="CA">CA</option>
            </FieldSelect>
            <FieldInput placeholder="Phone *" value={shippingForm.phone} onChange={(e) => setShippingForm((s) => ({ ...s, phone: e.target.value }))} />
          </>
        )}
        <div>
          <OverrideButton
            disabled={
              isUkOrIe
                ? !pickupStudioId
                : !(
                    shippingForm.address1
                    && shippingForm.city
                    && shippingForm.state
                    && shippingForm.postal_code
                  )
            }
            onClick={() => setMode('confirm')}
          >
            Next: review
          </OverrideButton>
          <SecondaryButton onClick={() => setMode('prize')}>Back</SecondaryButton>
        </div>
      </WizardBody>
    )
  }

  const renderConfirmStep = () => {
    if (!region) return null
    const minCountForPrize = selectedTrackMinCount
      || trackMinCountFromRegistration(reg)
      || 12
    return (
      <WizardBody>
        <FieldLabel>Review</FieldLabel>
        <InlineNotice kind="info">
          About to: {!isCompleted && selectedTrackMinCount ? `complete at ${selectedTrackMinCount} classes, then ` : ''}
          award <b>{selectedPrizeCode}</b>
          {selectedPrizeCode === 'gift_card' ? ` (${region.currency} ${giftCardAmountFor(region, teamSize, minCountForPrize)})` : ` (${skuForBag(teamSize, minCountForPrize)})`}
          {selectedPrizeCode === 'bag' && (region.country === 'GB' || region.country === 'IE')
            ? `, pickup at ${UWB_PICKUP_STUDIOS.flatMap((g) => g.studios).find((s) => s.id === pickupStudioId)?.name || '(no studio)'}`
            : ''}
          {selectedPrizeCode === 'bag' && !(region.country === 'GB' || region.country === 'IE')
            ? `, ship to ${shippingForm.address1}, ${shippingForm.city}, ${shippingForm.state} ${shippingForm.postal_code} ${shippingForm.country}`
            : ''}
          , then fulfill — for MT user {marianaTekData?.data?.[0]?.id || marianaTekData?.user?.external_id || '—'} ({email}).
        </InlineNotice>
        {wizardError && <InlineNotice kind="err">{wizardError}</InlineNotice>}
        <div>
          <OverrideButton onClick={runSubmit}>{canAdminOverride ? 'Submit override' : 'Award prize'}</OverrideButton>
          <SecondaryButton onClick={() => setMode(selectedPrizeCode === 'bag' ? 'details' : 'prize')}>Back</SecondaryButton>
          <SecondaryButton onClick={resetWizard}>Cancel</SecondaryButton>
        </div>
      </WizardBody>
    )
  }

  const renderRunning = () => (
    <WizardBody>
      <InlineNotice kind="info">Processing prize — do not close this tab.</InlineNotice>
      <StepLog>{stepLog.join('\n')}</StepLog>
      {wizardError && <InlineNotice kind="err">{wizardError}</InlineNotice>}
      {wizardError && (
        <div>
          <SecondaryButton onClick={() => setMode('confirm')}>Back to review</SecondaryButton>
          <SecondaryButton onClick={resetWizard}>Close</SecondaryButton>
        </div>
      )}
    </WizardBody>
  )

  const renderDone = () => (
    <WizardBody>
      <InlineNotice kind="ok">Prize processing complete. Card refreshed.</InlineNotice>
      <StepLog>{stepLog.join('\n')}</StepLog>
      <div>
        <SecondaryButton onClick={resetWizard}>Close</SecondaryButton>
      </div>
    </WizardBody>
  )

  // Per-row UWB bag-order cancel handler. Wired into the new "Existing UWB
  // bag orders" section at the top of the change-prize picker. Two-stage:
  // first cancels in Shopify, then stamps prize_order_canceled_at in CDP.
  const cancelBagOrderRow = async (order: UwbBagOrder) => {
    const ok = window.confirm(
      'Cancel Shopify order ' + order.name + ' on ' + order.shop.toUpperCase() +
      '? This is irreversible. The customer will NOT be emailed.'
    )
    if (!ok) return
    setCancelingOrderId(order.id)
    appendLog(`POST /api/shopify/order-cancel (${order.shop}, ${order.id})`)
    try {
      const r = await cancelUwbBagOrder({
        order_id: order.id,
        shop: order.shop,
        admin_notes: 'UWB 2026 admin prize swap',
        adminEmail,
      })
      if (!r.ok) {
        appendLog(`  ✗ order cancel failed (${r.error_code || 'unknown'}): ${r.message || 'no message'}`)
        setCancelingOrderId(null)
        return
      }
      appendLog(`  ✓ order canceled (cancelled_at ${r.cancelled_at || 'unknown'})`)
      if (!registrationId) {
        appendLog(`  ✗ CDP update skipped — no registration id loaded`)
        setCancelingOrderId(null)
        return
      }
      appendLog(`POST /api/uwb/admin/registration/${registrationId}/cancel-bag-order`)
      const c = await cdpCancelBagOrder({
        registrationId,
        shopify_order_id: Number(order.id),
        shopify_shop: order.shop,
        canceled_notes: 'UWB 2026 admin prize swap',
        adminEmail,
      })
      if (c.ok) {
        appendLog(`  ✓ CDP stamped prize_order_canceled_at`)
      } else {
        appendLog(`  ✗ CDP update failed: ${c.message || 'unknown'} — Shopify already canceled; please reset the row manually`)
      }
      setCancelingOrderId(null)
      setMode('prize')
    } catch (e: any) {
      appendLog(`  ✗ cancel flow errored: ${e?.message || 'unknown'}`)
      setCancelingOrderId(null)
    }
  }

  const renderUwbOrdersSection = () => {
    if (uwbOrdersLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <FieldLabel>Existing UWB bag orders</FieldLabel>
          <InlineNotice kind="info">Looking up bag orders…</InlineNotice>
        </div>
      )
    }
    if (uwbOrdersError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <FieldLabel>Existing UWB bag orders</FieldLabel>
          <InlineNotice kind="err">UWB bag-order lookup failed: {uwbOrdersError}</InlineNotice>
        </div>
      )
    }
    if (!uwbOrders || uwbOrders.length === 0) {
      // Silently omit; fallback InlineNotice at the bottom handles the
      // "no artifacts at all" case.
      return null
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <FieldLabel>Existing UWB bag orders</FieldLabel>
        {uwbOrders.map((order) => {
          const fulfillment = order.fulfillment_status || 'unfulfilled'
          const summary = `#${order.name} — ${order.shop.toUpperCase()} — ${order.currency} ${order.total} — ${fulfillment} — created ${fmtDate(order.created_at)}`
          const buttonLabel = !order.can_cancel
            ? (order.block_reason || 'Cannot cancel')
            : (cancelingOrderId === order.id ? 'Canceling…' : 'Cancel this order')
          return (
            <RadioRow key={order.id}>
              <span style={{ flex: 1 }}>{summary}</span>
              <SecondaryButton
                disabled={!order.can_cancel || cancelingOrderId !== null}
                onClick={() => cancelBagOrderRow(order)}
              >
                {buttonLabel}
              </SecondaryButton>
            </RadioRow>
          )
        })}
      </div>
    )
  }

  const renderChangePrizeFallback = () => (
    <>
      <InlineNotice kind="info">
        No active UWB prize artifacts found for this customer. You can proceed
        to issue a new prize — but verify with the warehouse first that no bag
        was shipped under a different email.
      </InlineNotice>
      <div>
        <SecondaryButton onClick={() => setMode('prize')}>Continue to prize</SecondaryButton>
        <SecondaryButton onClick={resetWizard}>Cancel</SecondaryButton>
      </div>
    </>
  )

  const renderChangePrize = () => {
    const uwbOrdersSection = renderUwbOrdersSection()
    const activeGiftCardsCount = (existingGiftCards || []).filter(
      (c: any) => (c.status || '').toLowerCase() === 'active'
    ).length
    const showFallback =
      !uwbOrdersLoading
      && (uwbOrders?.length || 0) === 0
      && !giftCardCheckLoading
      && activeGiftCardsCount === 0

    if (reg?.prize_selected === 'gift_card' && isFulfilled) {
      if (giftCardCheckLoading && uwbOrdersLoading) {
        return (
          <WizardBody>
            {uwbOrdersSection}
            <InlineNotice kind="info">Checking the existing gift card's balance…</InlineNotice>
          </WizardBody>
        )
      }
      const cards = existingGiftCards || []

      // 1) Strong match: note explicitly references this registration id.
      const regIdMatches = registrationId
        ? cards.filter((c) => (c.note || '').toLowerCase().includes(String(registrationId).toLowerCase()))
        : []

      // 2) Heuristic for historic cards (no reg id in the note): match by
      //    expected amount + currency + active. The recency window is anchored
      //    to whichever timestamp is latest among prize_fulfilled_at,
      //    prize_selected_at and team_completed_at (issuance can lag any of
      //    them) and widened to ±14d. If the narrow window drops everything,
      //    we retry without the recency filter and flag a `recencyRelaxed`
      //    warning so the admin knows the picker is wider than usual.
      //
      //    When `region` can't be resolved, fall back to status+UWB-note so we
      //    never collapse to zero silently.
      let heuristicMatches: any[] = []
      let recencyRelaxed = false
      const minCount = trackMinCountFromRegistration(reg) || 12
      const expectedAmount = region ? giftCardAmountFor(region, teamSize, minCount) : null
      const expectedCurrency = region?.currency || null
      const anchorMs = (() => {
        const candidates = [reg?.prize_fulfilled_at, reg?.prize_selected_at, reg?.team_completed_at]
          .map((s) => (s ? new Date(s).getTime() : NaN))
          .filter((n) => Number.isFinite(n)) as number[]
        return candidates.length ? Math.max(...candidates) : null
      })()
      const RECENCY_WINDOW_MS = 14 * 24 * 60 * 60 * 1000
      if (regIdMatches.length === 0) {
        const filterCore = (c: any) => {
          const statusOk = (c.status || '').toLowerCase() === 'active'
          if (!statusOk) return false
          if (region) {
            const initialOk = expectedAmount == null
              || Math.abs(parseFloat(c.initial_value || '0') - expectedAmount) < 0.01
            const currencyOk = !expectedCurrency
              || (c.currency || '').toUpperCase() === expectedCurrency.toUpperCase()
            return initialOk && currencyOk
          }
          // No region resolved → soft filter on UWB-ish note.
          return (c.note || '').toLowerCase().includes('uwb')
        }
        const withRecency = cards.filter((c) => {
          if (!filterCore(c)) return false
          if (!anchorMs || !c.created_at) return true
          const created = new Date(c.created_at).getTime()
          return Math.abs(created - anchorMs) <= RECENCY_WINDOW_MS
        })
        if (withRecency.length > 0) {
          heuristicMatches = withRecency
        } else {
          // Retry without the recency filter — surfaces every active card that
          // otherwise passes amount/currency (or note-includes-uwb when region
          // is null) so the admin sees a real picker instead of a dead end.
          const withoutRecency = cards.filter(filterCore)
          if (withoutRecency.length > 0) {
            heuristicMatches = withoutRecency
            recencyRelaxed = true
          }
        }
      }

      const autoMatches = regIdMatches.length > 0 ? regIdMatches : heuristicMatches

      // Fallback list: every active card on file for this email, sorted by
      // created_at desc. We always render a picker — never a dead-end bailout.
      const allActive = cards
        .filter((c) => (c.status || '').toLowerCase() === 'active')
        .slice()
        .sort((a, b) => {
          const at = a.created_at ? new Date(a.created_at).getTime() : 0
          const bt = b.created_at ? new Date(b.created_at).getTime() : 0
          return bt - at
        })

      // What we'll actually render in the picker.
      const usingFallback = autoMatches.length === 0
      const renderList = usingFallback ? allActive : autoMatches

      // `label` is a human-readable identifier (last4 + amount) so the StepLog
      // audit reads "Disabled card •••• 1234 — $90 USD" instead of a 12-digit
      // Shopify id. Falls back to the raw id when we don't have card metadata
      // (e.g. manual-paste of an id we couldn't find in the lookup).
      const disableCard = async (cardId: string | number, currency: string, label?: string) => {
        setWizardError('')
        const id = String(cardId)
        const tag = label || id
        const r = await postJSON(`/api/shopify/gift-cards/${id}/disable`, { currency: currency || 'USD' })
        if (!r.ok) {
          setWizardError(`disable failed (${r.status}): ${r.body?.error || 'unknown'}`)
          appendLog(`✗ disable card ${tag} (id ${id}) failed (${r.status}): ${r.body?.error || 'unknown'}`)
          return false
        }
        appendLog(`✓ disabled gift card ${tag} (id ${id})`)
        return true
      }

      // Resolve the right human-readable label for a picker row.
      const labelFor = (c: any) => {
        const init = parseFloat(c?.initial_value || '0')
        const last4 = c?.code_last4 ? `•••• ${c.code_last4}` : `id ${c?.id || '?'}`
        const ccy = c?.currency || ''
        return `${last4} — ${ccy} ${init.toFixed(2)}`
      }

      const expectedSummary = region && expectedAmount != null
        ? `${expectedCurrency} ${expectedAmount.toFixed(2)} for the ${minCount}-class track, within ±14d of ${anchorMs ? fmtDate(new Date(anchorMs).toISOString()) : 'completion'}`
        : 'status=active + UWB note (region could not be resolved from this registration)'

      return (
        <WizardBody>
          {uwbOrdersSection}
          {showFallback && (
            <InlineNotice kind="info">
              No active UWB prize artifacts found for this customer. You can
              proceed to issue a new prize — but verify with the warehouse
              first that no bag was shipped under a different email.
            </InlineNotice>
          )}
          {regIdMatches.length > 0 ? (
            <InlineNotice kind="info">
              Matched gift card{regIdMatches.length > 1 ? 's' : ''} by registration id in the note. Pick the one to disable and continue.
            </InlineNotice>
          ) : autoMatches.length > 1 ? (
            <InlineNotice kind="warn">
              {autoMatches.length} active gift cards plausibly match this registration ({expectedSummary}). Pick the one to disable.
            </InlineNotice>
          ) : autoMatches.length === 1 ? (
            <InlineNotice kind="warn">
              Found one candidate by heuristic match ({expectedSummary}). Confirm before disabling — we won't auto-disable.
            </InlineNotice>
          ) : allActive.length > 0 ? (
            <InlineNotice kind="warn">
              No card auto-matched ({expectedSummary}). Showing all {allActive.length} active gift card{allActive.length === 1 ? '' : 's'} on file for {email || 'this customer'} — pick the one to disable, paste a different card ID below, or skip this step.
            </InlineNotice>
          ) : (
            <InlineNotice kind="warn">
              No active Shopify gift cards found for {email || 'this customer'} across US/UK/IE. The card may have been issued to a different email (team-member-vs-captain mismatch) or via a non-admin-site path. Paste a card ID below or skip this step.
            </InlineNotice>
          )}

          {recencyRelaxed && renderList.length > 0 && (
            <InlineNotice kind="warn">
              No card matched inside the ±14d recency window — showing matches outside that window. Double-check the created date before disabling.
            </InlineNotice>
          )}

          {renderList.map((c) => {
            const bal = parseFloat(c.balance || '0')
            const init = parseFloat(c.initial_value || '0')
            const cardSpent = init > bal
            const noteSnippet = (c.note || '').toString().slice(0, 120)
            return (
              <RadioRow key={c.id}>
                <span style={{ flex: 1 }}>
                  •••• {c.code_last4 || '????'} — {c.currency} {init.toFixed(2)} (balance {bal.toFixed(2)})
                  {c.created_at ? ` — created ${fmtDate(c.created_at)}` : ''}
                  {c.store ? ` — ${String(c.store).toUpperCase()}` : ''} — {c.status}
                  {cardSpent ? ' — partially spent' : ''}
                  {noteSnippet ? <div style={{ fontSize: '1.1rem', color: '#666', marginTop: '0.2rem' }}>note: {noteSnippet}</div> : null}
                </span>
                <SecondaryButton
                  disabled={cardSpent}
                  onClick={async () => {
                    const ok = await disableCard(c.id, c.currency, labelFor(c))
                    if (ok) setMode('prize')
                  }}
                >
                  {cardSpent ? 'Spent — cannot disable' : 'Disable this card'}
                </SecondaryButton>
              </RadioRow>
            )
          })}

          {/* Manual-paste fallback: always available so the wizard is never a dead end. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
            <FieldLabel htmlFor="uwb-manual-card-id">
              Paste a Shopify gift card ID manually
            </FieldLabel>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <FieldInput
                id="uwb-manual-card-id"
                type="text"
                placeholder="e.g. 1234567890123"
                value={manualCardId}
                onChange={(e) => setManualCardId(e.target.value)}
                style={{ flex: 1 }}
              />
              <FieldSelect
                value={manualCardCurrency}
                onChange={(e) => setManualCardCurrency(e.target.value as 'USD' | 'GBP' | 'EUR')}
              >
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
              </FieldSelect>
              <SecondaryButton
                disabled={!manualCardId.trim()}
                onClick={async () => {
                  const id = manualCardId.trim()
                  if (!id) return
                  // Cross-check the pasted id against the looked-up list, when
                  // available. If it's in there, use the known metadata and
                  // block disable-of-spent-card. If it's NOT in the list, ask
                  // the admin to confirm before firing the disable POST.
                  const known = (existingGiftCards || []).find((c: any) => String(c?.id) === id)
                  if (known) {
                    const init = parseFloat(known.initial_value || '0')
                    const bal = parseFloat(known.balance || '0')
                    if (init > bal) {
                      setWizardError(`gift card ${labelFor(known)} is partially spent (${bal.toFixed(2)} of ${init.toFixed(2)}); cannot disable a spent card`)
                      return
                    }
                    const ok = await disableCard(id, known.currency || manualCardCurrency, labelFor(known))
                    if (ok) setMode('prize')
                    return
                  }
                  // Pasted id wasn't in this customer's gift-card list — confirm.
                  if (!window.confirm(`Card id ${id} is not in this customer's gift-card list. Disable anyway?`)) return
                  const ok = await disableCard(id, manualCardCurrency, `id ${id} (manual paste, not in lookup)`)
                  if (ok) setMode('prize')
                }}
              >
                Disable & continue
              </SecondaryButton>
            </div>
          </div>

          {wizardError && <InlineNotice kind="err">{wizardError}</InlineNotice>}
          {stepLog.length > 0 && <StepLog>{stepLog.join('\n')}</StepLog>}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <SecondaryButton
              onClick={() => {
                appendLog('⚠ skipped disable step — admin will handle previously-issued card manually in Shopify Admin')
                setMode('prize')
              }}
            >
              Skip disable, continue to swap
            </SecondaryButton>
            <SecondaryButton onClick={resetWizard}>Cancel</SecondaryButton>
          </div>
        </WizardBody>
      )
    }
    // Bag-fulfilled OR any other change_prize entry: render the new UWB
    // bag-orders section at the top (when there's something to show), and
    // surface a fallback InlineNotice when neither bag orders nor active
    // gift cards exist so the wizard never dead-ends.
    return (
      <WizardBody>
        {uwbOrdersSection}
        {wizardError && <InlineNotice kind="err">{wizardError}</InlineNotice>}
        {stepLog.length > 0 && <StepLog>{stepLog.join('\n')}</StepLog>}
        {showFallback ? (
          renderChangePrizeFallback()
        ) : (uwbOrders?.length || 0) > 0 || uwbOrdersLoading ? (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <SecondaryButton onClick={() => setMode('prize')}>Continue to prize</SecondaryButton>
            <SecondaryButton onClick={resetWizard}>Cancel</SecondaryButton>
          </div>
        ) : null}
      </WizardBody>
    )
  }

  // ── Main render ────────────────────────────────────────────────
  return (
    <Card>
      <Title>United We Barry's</Title>
      {loading && <Muted>Checking registration…</Muted>}
      {!loading && error && <Muted>{error}</Muted>}
      {!loading && !error && data && !data.registered && (
        <Muted>Not registered for United We Barry's.</Muted>
      )}
      {!loading && !error && data && data.registered && reg && (
        <>
          <Grid>
            <Stat>
              <StatLabel>Status</StatLabel>
              <StatValue>
                <Pill kind="yes">Registered</Pill>
              </StatValue>
            </Stat>
            <Stat>
              <StatLabel>Track</StatLabel>
              <StatValue>{reg.selected_track || (reg.selected_min_count ? `${reg.selected_min_count} classes` : null) || reg.current_track || reg.team_current_track || '—'}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Individual classes</StatLabel>
              <StatValue>{reg.individual_count ?? 0}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Team</StatLabel>
              <StatValue>
                {reg.team_size > 1 ? `${reg.team_name || 'Team'} (${reg.team_size})` : 'Solo'}
              </StatValue>
            </Stat>
            {reg.team_size > 1 && (
              <Stat>
                <StatLabel>Team classes</StatLabel>
                <StatValue>{reg.team_progress_count ?? 0}</StatValue>
              </Stat>
            )}
            <Stat>
              <StatLabel>Completed</StatLabel>
              <StatValue>
                {reg.team_completed_at ? `✓ ${reg.team_completed_track || 'finished'}` : 'Not yet'}
              </StatValue>
            </Stat>
            {reg.team_completed_at && (
              <>
                <Stat>
                  <StatLabel>Finished on</StatLabel>
                  <StatValue>{fmtDate(reg.team_completed_at) || '—'}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Prize selected</StatLabel>
                  <StatValue>
                    {reg.prize_selected
                      ? `${reg.prize_selected}${reg.prize_selected_at ? ` · ${fmtDate(reg.prize_selected_at)}` : ''}`
                      : 'Not selected yet'}
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Prize fulfilled</StatLabel>
                  <StatValue>
                    {reg.prize_fulfilled_at ? `✓ ${fmtDate(reg.prize_fulfilled_at)}` : 'Pending'}
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Finisher email sent</StatLabel>
                  <StatValue>
                    {finisherLoading
                      ? 'Checking…'
                      : finisher?.found
                      ? fmtDate(finisher.sent_at) || '✓ sent'
                      : 'Not found yet'}
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Opened</StatLabel>
                  <StatValue>
                    {finisherLoading
                      ? 'Checking…'
                      : finisher?.found && finisher.opened
                      ? `✓ ${fmtDate(finisher.opened_at) || 'yes'}`
                      : finisher?.found
                      ? 'Not yet'
                      : '—'}
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Clicked</StatLabel>
                  <StatValue>
                    {finisherLoading
                      ? 'Checking…'
                      : finisher?.found && finisher.clicked
                      ? <Pill kind="yes">Yes</Pill>
                      : finisher?.found
                      ? 'No'
                      : '—'}
                  </StatValue>
                </Stat>
              </>
            )}
          </Grid>

          {(canAdminOverride || isAdmin) && (
            <OverrideSection>
              <OverrideTitle>{canAdminOverride ? 'Override & Award Prize' : 'Award Prize'}</OverrideTitle>
              {!adminOverrideConfigured ? (
                <InlineNotice kind="err">
                  Prize override is unavailable because the dedicated CDP admin credential is not configured.
                </InlineNotice>
              ) : !canAdminOverride && !isCompleted ? (
                <InlineNotice kind="info">Prize fulfillment becomes available after the team completes a qualifying track.</InlineNotice>
              ) : !canAdminOverride && isFulfilled ? (
                <InlineNotice kind="info">Prize already fulfilled. Changes that revoke a card or cancel an order require an authorized override operator.</InlineNotice>
              ) : mode === 'idle' && (
                <>
                  <Muted style={{ marginBottom: '0.75rem' }}>
                    {isFulfilled
                      ? 'Prize already fulfilled. Use Change prize to swap (gift-card swaps require the original to be unspent).'
                      : isCompleted && hasPrize
                      ? 'Already completed and prize chosen — open to walk through fulfillment.'
                      : isCompleted
                      ? 'Already completed — pick a prize for them.'
                      : 'Force-complete this registration and award a prize.'}
                  </Muted>
                  {isFulfilled ? (
                    // Once fulfilled, the only meaningful CTA is a prize swap.
                    // The previous "View / Fulfilled" label was misleading
                    // because clicking it actually entered change_prize mode.
                    <OverrideButton onClick={startChangePrize}>Change prize</OverrideButton>
                  ) : (
                    <>
                      <OverrideButton onClick={startOverride}>
                        {isCompleted && hasPrize ? 'Continue fulfillment' : isCompleted ? 'Award prize' : 'Override / Award Prize'}
                      </OverrideButton>
                      {hasPrize && canAdminOverride && (
                        <SecondaryButton onClick={startChangePrize}>Change prize</SecondaryButton>
                      )}
                      {hasPrize && !canAdminOverride && isCompleted && !isFulfilled && (
                        <SecondaryButton onClick={() => {
                          resetWizard()
                          setSelectedTrackMinCount(trackMinCountFromRegistration(reg))
                          setMode('prize')
                        }}>Change prize selection</SecondaryButton>
                      )}
                    </>
                  )}
                </>
              )}
              {adminOverrideConfigured && canAdminOverride && mode === 'track' && renderTrackStep()}
              {adminOverrideConfigured && mode === 'prize' && renderPrizeStep()}
              {adminOverrideConfigured && mode === 'details' && renderDetailsStep()}
              {adminOverrideConfigured && mode === 'confirm' && renderConfirmStep()}
              {adminOverrideConfigured && mode === 'running' && renderRunning()}
              {adminOverrideConfigured && mode === 'done' && renderDone()}
              {adminOverrideConfigured && canAdminOverride && mode === 'change_prize' && renderChangePrize()}
            </OverrideSection>
          )}
        </>
      )}
    </Card>
  )
}

export default UwbStatusCard
